import { useState, useCallback } from "react";
import LeadsService from "@/services/LeadsService";

export default function useLeadsData() {
  const defaultLimit = 5;
  const limitIncrement = 50;
  const defaultSortColumn = "created";
  const defaultSortDirection = "desc";

  // Data State
  const [data, setData] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [limit, setLimit] = useState(defaultLimit);
  
  // Filter/Sort State
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({});

  /**
   * Load leads data using the service
   */
  const loadData = useCallback(async () => {
    if (!loadingMore) setLoading(true);
    setError(null);

    // Use the LeadsService to get leads with filters
    const result = await LeadsService.getLeadsWithFilters({
      limit,
      sortColumn,
      sortDirection,
      filters: {
        ...filters,
        searchText: searchQuery
      }
    });
    
    if (result.error) {
      setError(result.error);
      setData([]);
      setTotalCount(0);
      setHasMore(false);
    } else {
      setData(result.data);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
      setSearching(searchQuery.length >= 3);
    }
    
    setLoading(false);
    setLoadingMore(false);
  }, [limit, sortColumn, sortDirection, searchQuery, filters, loadingMore]);

  /**
   * Load more leads (pagination)
   */
  const handleLoadMore = useCallback(() => {
    if (hasMore && !loadingMore) {
      setLimit((prevLimit) => prevLimit + limitIncrement);
      setLoadingMore(true);
    }
  }, [hasMore, loadingMore, limitIncrement]);

  /**
   * Handle search query changes
   */
  const handleSearch = useCallback((query) => {
    // console.log(`Setting search query to: ${query}`);
    setSearchQuery(query);
  }, [defaultLimit]);

  /**
   * Update sorting
   */
  const setSorting = useCallback((column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
    setLoading(true);
  }, [defaultLimit]);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    // console.log('Updating filters:', newFilters);
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  }, [defaultLimit]);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({});
    setLimit(defaultLimit);
  }, [defaultLimit]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get lead by ID
   */
  const getLeadById = useCallback(async (leadId) => {
    setLoading(true);
    setError(null);

    const result = await LeadsService.getLeadById(leadId);
    
    setLoading(false);
    
    if (result.error) {
      setError(result.error);
      return null;
    }
    
    return result.data;
  }, []);

  /**
   * Get leads by customer - TODO: Implement in LeadsService
   */
  const getLeadsByCustomer = useCallback(async (customerIdentifier) => {
    setLoading(true);
    setError(null);

    // TODO: Add getLeadsByCustomer method to LeadsService
    console.warn('getLeadsByCustomer not implemented in LeadsService');
    
    setLoading(false);
    return [];
  }, []);

  /**
   * Create a new lead
   */
  const createLead = useCallback(async (leadData) => {
    setLoading(true);
    setError(null);

    const result = await LeadsService.createLead(leadData);
    
    setLoading(false);
    
    if (!result.success) {
      setError(result.error);
      return { success: false, error: result.error };
    }
    
    // Refresh data after successful creation
    await loadData();
    
    return { success: true, data: result.data };
  }, [loadData]);

  /**
   * Update an existing lead
   */
  const updateLead = useCallback(async (leadId, leadData) => {
    setLoading(true);
    setError(null);

    const result = await LeadsService.updateLead(leadId, leadData);
    
    setLoading(false);
    
    if (!result.success) {
      setError(result.error);
      return { success: false, error: result.error };
    }
    
    // Refresh data after successful update
    await loadData();
    
    return { success: true, data: result.data };
  }, [loadData]);

  /**
   * Delete a lead
   */
  const deleteLead = useCallback(async (leadId) => {
    setLoading(true);
    setError(null);

    // Use deleteLeads method with single ID array
    const result = await LeadsService.deleteLeads([leadId]);
    
    setLoading(false);
    
    if (!result.success) {
      setError(result.error);
      return { success: false, error: result.error };
    }
    
    // Refresh data after successful deletion
    await loadData();
    
    return { success: true, message: result.message };
  }, [loadData]);

  /**
   * Delete multiple leads
   */
  const deleteLeads = useCallback(async (leadIds) => {
    setLoading(true);
    setError(null);

    const result = await LeadsService.deleteLeads(leadIds);
    
    setLoading(false);
    
    if (!result.success) {
      setError(result.error);
      return { success: false, error: result.error };
    }
    
    // Refresh data after successful deletion
    await loadData();
    
    return { success: true, message: result.message };
  }, [loadData]);

  /**
   * Get lead statistics
   */
  const getLeadStats = useCallback(async () => {
    const result = await LeadsService.getLeadStats();
    
    if (result.error) {
      setError(result.error);
      return {};
    }
    
    return result.data;
  }, []);

  /**
   * Add communication to a lead - TODO: Implement in LeadsService
   */
  const addCommunication = useCallback(async (communicationData) => {
    setLoading(true);
    setError(null);

    // TODO: Add addCommunication method to LeadsService
    console.warn('addCommunication not implemented in LeadsService');
    
    setLoading(false);
    return { success: false, error: 'Method not implemented' };
  }, []);

  /**
   * Get communications for a lead - TODO: Implement in LeadsService
   */
  const getCommunications = useCallback(async (leadId) => {
    // TODO: Add getCommunications method to LeadsService
    console.warn('getCommunications not implemented in LeadsService');
    return [];
  }, []);

  /**
   * Get available status options - TODO: Implement in LeadsService
   */
  const getStatusOptions = useCallback(() => {
    // TODO: Add getStatusOptions method to LeadsService
    console.warn('getStatusOptions not implemented in LeadsService');
    return [];
  }, []);

  /**
   * Get available source options - TODO: Implement in LeadsService
   */
  const getSourceOptions = useCallback(() => {
    // TODO: Add getSourceOptions method to LeadsService
    console.warn('getSourceOptions not implemented in LeadsService');
    return [];
  }, []);

  /**
   * Validate lead data - TODO: Implement in LeadsService
   */
  const validateLead = useCallback((leadData) => {
    // TODO: Add validateLead method to LeadsService
    console.warn('validateLead not implemented in LeadsService');
    return { valid: true, errors: [] };
  }, []);

  return {
    // Data
    data,
    totalCount,
    hasMore,
    
    // UI States
    loading,
    loadingMore,
    searching,
    error,
    
    // Filter/Sort State
    searchQuery,
    sortColumn,
    sortDirection,
    filters,
    limit,
    
    // Basic Actions
    loadData,
    handleLoadMore,
    handleSearch,
    setSorting,
    updateFilters,
    clearFilters,
    
    // CRUD Operations
    getLeadById,
    getLeadsByCustomer,
    createLead,
    updateLead,
    deleteLead,
    deleteLeads,
    
    // Additional Features
    getLeadStats,
    addCommunication,
    getCommunications,
    
    // Utilities
    getStatusOptions,
    getSourceOptions,
    validateLead,
    
    // State management
    setError: clearError,
  };
}
